export interface ClipRecorder {
  arm(): void;
  disarm(): void;
  readonly armed: boolean;
  save(): Promise<boolean>;
  destroy(): void;
}

const MIME_CANDIDATES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
const RING_SIZE = 12;
const TIMESLICE_MS = 1000;

function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

export function createClipRecorder(canvas: HTMLCanvasElement, audio: AudioNode): ClipRecorder | null {
  if (typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') return null;
  const mimeType = pickMimeType();
  if (mimeType === null) return null;

  const context = audio.context as AudioContext;
  const audioDest = context.createMediaStreamDestination();
  audio.connect(audioDest);

  const videoStream = canvas.captureStream(60);
  const stream = new MediaStream([...videoStream.getVideoTracks(), ...audioDest.stream.getAudioTracks()]);

  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let armed = false;
  let pendingUrl: string | null = null;

  function onData(event: BlobEvent): void {
    if (event.data.size === 0) return;
    chunks.push(event.data);
    if (chunks.length > RING_SIZE) chunks.shift();
  }

  function stopRecorder(): Promise<void> {
    const active = recorder;
    if (active === null) return Promise.resolve();
    if (active.state === 'inactive') {
      active.removeEventListener('dataavailable', onData);
      recorder = null;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      active.addEventListener('stop', () => resolve(), { once: true });
      try {
        active.stop();
      } catch {
        resolve();
      }
    }).then(() => {
      active.removeEventListener('dataavailable', onData);
      recorder = null;
    });
  }

  function revokePending(): void {
    if (pendingUrl === null) return;
    URL.revokeObjectURL(pendingUrl);
    pendingUrl = null;
  }

  return {
    get armed() {
      return armed;
    },
    arm() {
      if (armed) return;
      chunks = [];
      try {
        recorder = new MediaRecorder(stream, { mimeType });
        recorder.addEventListener('dataavailable', onData);
        recorder.start(TIMESLICE_MS);
        armed = true;
      } catch {
        recorder = null;
        armed = false;
      }
    },
    disarm() {
      if (!armed) return;
      armed = false;
      void stopRecorder();
      chunks = [];
    },
    async save() {
      if (recorder === null || chunks.length === 0) return false;
      await stopRecorder();
      armed = false;
      if (chunks.length === 0) return false;
      const blob = new Blob(chunks, { type: mimeType });
      const file = new File([blob], 'solta.webm', { type: mimeType });
      if (navigator.canShare?.({ files: [file] }) === true) {
        try {
          await navigator.share({ files: [file] });
          return true;
        } catch {
          // user cancelled or share failed — fall back to download
        }
      }
      revokePending();
      const url = URL.createObjectURL(blob);
      pendingUrl = url;
      const link = document.createElement('a');
      link.href = url;
      link.download = 'solta.webm';
      link.click();
      revokePending();
      return true;
    },
    destroy() {
      armed = false;
      void stopRecorder();
      chunks = [];
      try {
        audio.disconnect(audioDest);
      } catch {
        // already disconnected — ignore
      }
      revokePending();
    },
  };
}
